import { useState, useEffect, useCallback } from 'react';
import { useApi } from '../api';
import { useTranslation } from 'react-i18next';
import { Product, ProductSortBy, ProductSortDirection } from '../components/products/types';

interface UseProductsReturn {
  products: Product[];
  loading: boolean;
  error: string | null;
  query: string;
  setQuery: (query: string) => void;
  sortBy: ProductSortBy;
  sortDirection: ProductSortDirection;
  setSort: (sortBy: ProductSortBy) => void;
  refetch: () => Promise<void>;
  // Pagination properties
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  setPage: (page: number) => void;
  setPageSize: (pageSize: number) => void;
}

const SEARCH_DEBOUNCE_MS = 300;
const DEFAULT_PAGE_SIZE = 25;
const DEFAULT_SORT_BY: ProductSortBy = 'updated_at';
const DEFAULT_SORT_DIRECTION: ProductSortDirection = 'desc';

export const useProducts = (): UseProductsReturn => {
  const api = useApi();
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [sortBy, setSortBy] = useState<ProductSortBy>(DEFAULT_SORT_BY);
  const [sortDirection, setSortDirection] = useState<ProductSortDirection>(DEFAULT_SORT_DIRECTION);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [totalCount, setTotalCount] = useState(0);

  const totalPages = Math.ceil(totalCount / pageSize);

  const fetchProducts = useCallback(async (signal?: AbortSignal) => {
    try {
      setLoading(true);
      setError(null);
      const queryParams = new URLSearchParams({
        page: page.toString(),
        size: pageSize.toString(),
        sort_by: sortBy,
        sort_dir: sortDirection,
      });
      
      if (query) {
        queryParams.append('query', query);
      }

      const response = await api.getResponse(`/products?${queryParams.toString()}`, { signal });
      const totalCountHeader = response.headers.get('x-total-count');
      const total = totalCountHeader ? parseInt(totalCountHeader, 10) : 0;
      const data = await response.json();

      if (signal?.aborted) return;

      setProducts(Array.isArray(data) ? data : []);
      setTotalCount(total);
    } catch (err: unknown) {
      if (signal?.aborted) return;
      setError(t('errors.fetchFailed'));
      setProducts([]);
      setTotalCount(0);
    } finally {
      if (signal?.aborted) return;
      setLoading(false);
    }
  }, [query, sortBy, sortDirection, page, pageSize, api, t]);

  const refetch = useCallback(async () => {
    await fetchProducts();
  }, [fetchProducts]);

  const handleQueryChange = useCallback((nextQuery: string) => {
    setQuery(nextQuery);
    setPage(1);
  }, []);

  const handleSort = useCallback((nextSortBy: ProductSortBy) => {
    setSortBy((currentSortBy) => {
      if (currentSortBy !== nextSortBy) {
        setSortDirection(nextSortBy === 'updated_at' ? 'desc' : 'asc');
        return nextSortBy;
      }

      setSortDirection((currentDirection) => (currentDirection === 'asc' ? 'desc' : 'asc'));
      return currentSortBy;
    });
    setPage(1);
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => {
      fetchProducts(controller.signal);
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      controller.abort();
      clearTimeout(timeoutId);
    };
  }, [fetchProducts]);

  return {
    products,
    loading,
    error,
    query,
    setQuery: handleQueryChange,
    sortBy,
    sortDirection,
    setSort: handleSort,
    refetch,
    page,
    pageSize,
    totalCount,
    totalPages,
    setPage,
    setPageSize,
  };
};
